import { Template, SectionFieldOption } from '../_types';

/**
 * Helper function to sync field option IDs from saved field to editable field
 * Matches editable options with saved options and updates IDs accordingly
 */
export const syncFieldOptionIds = (
  editableOptions: SectionFieldOption[] | undefined,
  savedOptions: SectionFieldOption[] | undefined
): SectionFieldOption[] => {
  if (!editableOptions || !savedOptions) return editableOptions || [];

  // Track which saved options we've matched (by id) to handle new options
  const matchedSavedOptionIds = new Set<number>();

  return editableOptions.map((editableOption) => {
    // Already has a valid ID - just verify it still exists
    if (editableOption.id && editableOption.id !== 0) {
      const savedOption = savedOptions.find((so) => so.id === editableOption.id);
      if (savedOption && savedOption.id) {
        matchedSavedOptionIds.add(savedOption.id);
        return editableOption;
      }
    }

    // For inherited options (has fieldOptionId), match by fieldOptionId
    if (editableOption.fieldOptionId) {
      const savedOption = savedOptions.find(
        (so) => so.fieldOptionId === editableOption.fieldOptionId && !matchedSavedOptionIds.has(so.id || 0)
      );
      if (savedOption && savedOption.id) {
        matchedSavedOptionIds.add(savedOption.id);
        return { ...editableOption, id: savedOption.id };
      }
    }

    // For custom options (no fieldOptionId, id: 0), match by label
    if (!editableOption.fieldOptionId && (!editableOption.id || editableOption.id === 0)) {
      const savedOption = savedOptions.find(
        (so) =>
          !so.fieldOptionId &&
          so.label === editableOption.label &&
          so.status === 'Active' &&
          so.id &&
          !matchedSavedOptionIds.has(so.id)
      );
      if (savedOption && savedOption.id) {
        matchedSavedOptionIds.add(savedOption.id);
        return { ...editableOption, id: savedOption.id };
      }
    }

    return editableOption;
  });
};

/**
 * Return type for syncTemplateIds function
 */
export interface SyncTemplateIdsResult {
  updatedTemplate: Template;
  sectionIdMap: Map<number, number>;
  sectionGuidMap: Map<string, string>; // Maps old temp sectionGuid to new sectionGuid
  fieldIdMap: Map<number, number>;
}

/**
 * Helper function to sync IDs from saved template to editable template
 * Returns updated template and ID mappings for updating selections
 * Note: When a new version is created, ALL sections get new IDs, so we must match by sectionGuid
 */
export const syncTemplateIds = (
  editableTemplate: Template,
  savedTemplate: Template
): SyncTemplateIdsResult => {
  const sectionIdMap = new Map<number, number>();
  const sectionGuidMap = new Map<string, string>();
  const fieldIdMap = new Map<number, number>();

  // Get all existing sectionGuids (excluding temp ones) to find truly new sections from backend
  // We use sectionGuid instead of ID because IDs change when a new version is created
  const existingSectionGuids = new Set(
    editableTemplate.templateSections
      .filter((s) => s.id !== 0 && !s.sectionGuid.startsWith('temp-section-'))
      .map((s) => s.sectionGuid)
  );

  // Track which saved section GUIDs we've already matched (for handling multiple id===0 sections)
  const matchedSavedSectionGuids = new Set<string>();

  const updatedTemplate: Template = {
    ...editableTemplate,
    templateSections: editableTemplate.templateSections.map((editableSection) => {
      if (editableSection.id === 0) {
        const savedSection = savedTemplate.templateSections.find(
          (s) => !existingSectionGuids.has(s.sectionGuid) && !matchedSavedSectionGuids.has(s.sectionGuid)
        );

        if (savedSection) {
          matchedSavedSectionGuids.add(savedSection.sectionGuid);
          sectionIdMap.set(0, savedSection.id);
          // Map old temp sectionGuid to new sectionGuid for updating selected section
          sectionGuidMap.set(editableSection.sectionGuid, savedSection.sectionGuid);

          return {
            ...editableSection,
            id: savedSection.id,
            sectionGuid: savedSection.sectionGuid, // Sync sectionGuid from backend
            sectionFields: editableSection.sectionFields.map((editableField) => {
              const savedField = savedSection.sectionFields.find((f) => f.fieldId === editableField.fieldId);

              if (savedField && editableField.id === 0) {
                fieldIdMap.set(editableField.fieldId, savedField.id);
                return {
                  ...editableField,
                  id: savedField.id,
                  sectionFieldOptions: syncFieldOptionIds(editableField.sectionFieldOptions, savedField.sectionFieldOptions),
                };
              }
              if (savedField) {
                return {
                  ...editableField,
                  sectionFieldOptions: syncFieldOptionIds(editableField.sectionFieldOptions, savedField.sectionFieldOptions),
                };
              }
              return editableField;
            }),
          };
        }
      }

      // For existing sections, match by sectionGuid to sync IDs and field IDs
      // This handles the case where section IDs change when a new version is created
      const savedSection = savedTemplate.templateSections.find((s) => s.sectionGuid === editableSection.sectionGuid);

      if (savedSection) {
        return {
          ...editableSection,
          id: savedSection.id, // Sync section ID (may change when new version is created)
          sectionFields: editableSection.sectionFields.map((editableField) => {
            const savedField = savedSection.sectionFields.find((f) => f.fieldId === editableField.fieldId);

            if (savedField) {
              // Sync field ID (may change when new version is created, or be new field with id === 0)
              if (editableField.id === 0) {
                fieldIdMap.set(editableField.fieldId, savedField.id);
              }
              return {
                ...editableField,
                id: savedField.id,
                sectionFieldOptions: syncFieldOptionIds(editableField.sectionFieldOptions, savedField.sectionFieldOptions),
              };
            }
            return editableField;
          }),
        };
      }

      return editableSection;
    }),
  };

  return { updatedTemplate, sectionIdMap, sectionGuidMap, fieldIdMap };
};

